import React, { Component, createRef } from "react";
import PropTypes from "prop-types";
import { observable } from "mobx";
import { observer } from "mobx-react";
import { withTranslation } from "react-i18next";
import { Ref, Button, Icon, Segment, Grid, Tab, Form, Modal } from "semantic-ui-react";
import StickyMenu from "~/components/StickyMenu";
import GreetingBotStore from "~/stores/UiMeet3d/GreetingBotStore";
import ErrorWrapper from "~/components/wrapper/ErrorWrapper";
import ContentLoader from "~/components/element/ContentLoader";
import { getValidationErrors } from "~/utils/ReactReformed/ValidateSchema";
import GreetingBotGeneralForm from "~/modules/UiMeet3d/components/GreetingBotGeneralForm";
import GreetingBotAdvancedFormBuilder from "~/modules/UiMeet3d/components/GreetingBotAdvancedFormBuilder";
import GreetingBotSimpleFormBuilder from "~/modules/UiMeet3d/components/GreetingBotSimpleFormBuilder";
import GreetingBotFormPreview from "~/modules/UiMeet3d/components/GreetingBotFormPreview";
import CustomDndProvider from "~/components/CustomDndProvider";
import ToastMessageStore from "~/stores/ToastMessageStore";
import UserStore from "~/stores/UserStore";
import User from "~/models/User";
import ToastMessageContainer from "~/components/ToastMessageContainer";
import UiMeet3dLayoutWrapper from "~/modules/UiMeet3d/components/UiMeet3dLayoutWrapper";
import UiMeet3dNav from "~/modules/UiMeet3d/constants/UiMeet3dNav";
import UiMeet3dPhotoStore from "~/stores/UiMeet3d/UiMeet3dPhotoStore";
import FormStore from "~/stores/FormStore";
import UiMeet3dPhotoType from "~/constants/UiMeet3d/UiMeet3dPhotoType";
import LoadingWrapper from "~/components/wrapper/LoadingWrapper";
import UiMeet3dGreetingBotDefaultValueStore from "~/stores/UiMeet3d/UiMeet3dGreetingBotDefaultValueStore";

@observer
class GreetingBotPage extends Component {
  ref = null;

  @observable greetingBotStore = null;

  @observable formStore = null;

  @observable uiMeet3dPhotoStore = null;

  @observable uiMeet3dGreetingBotDefaultValueStore = null;

  constructor(props) {
    super(props);

    const { t, user, userStore } = props;

    this.state = {
      // show a warning whenever the user tries to add a custom field with a name already taken.
      showCustomFieldNameWarning: false,
      showCustomFieldEdit: false,
    };
    this.ref = createRef(null);
    document.title = `${t("uiMeet3d")} - ${t("clientPanelMisc:greetingBot")}`;
    this.greetingBotStore = new GreetingBotStore(user, userStore);
    this.formStore = new FormStore();
    this.uiMeet3dPhotoStore = new UiMeet3dPhotoStore();
    this.uiMeet3dGreetingBotDefaultValueStore = new UiMeet3dGreetingBotDefaultValueStore();
  }

  componentDidMount() {
    const { showError } = this.props;

    this.bindForm();

    this.uiMeet3dPhotoStore.loadIndex().catch(error => showError(error.message));
    this.uiMeet3dGreetingBotDefaultValueStore.loadIndex().catch(error => showError(error.message));

    this.greetingBotStore
      .loadOne()
      .then(() => {
        const { greetingBot } = this.greetingBotStore;
        this.greetingBotStore.bindGreetingBotToForm(greetingBot);
      })
      .catch(e => showError(e));
  }

  onSubmit() {
    const { showError, toastMessageStore, t } = this.props;
    const { model } = this.formStore;
    const { greetingBotAvatarPhotoData, deleteGreetingBotAvatarPhoto } = model;
    const { uiMeet3dPhotos } = this.uiMeet3dPhotoStore;

    const greetingBotSavePromise = this.greetingBotStore.saveOneFromStore().catch(e => showError(e));

    let greetingBotAvatarPhotoCreatePromise = Promise.resolve();
    let greetingBotAvatarPhotoDeletePromise = Promise.resolve();

    const greetingBotAvatarPhoto = this.uiMeet3dPhotoStore.getUiMeet3dPhotoByType(
      uiMeet3dPhotos,
      UiMeet3dPhotoType.GREETING_BOT_AVATAR,
    );

    if (deleteGreetingBotAvatarPhoto && greetingBotAvatarPhoto) {
      greetingBotAvatarPhotoDeletePromise = this.uiMeet3dPhotoStore
        .delete(greetingBotAvatarPhoto.uiMeet3dPhotoId)
        .then(() => {
          toastMessageStore.addToastMessage(t("clientPanelMisc:photoDeleted"), "positive", t("success"));
        })
        .catch(error => {
          if (error.status === "error") {
            showError(error.message);
          }
        });
    } else if (greetingBotAvatarPhotoData !== "") {
      greetingBotAvatarPhotoCreatePromise = this.uiMeet3dPhotoStore
        .create({
          type: UiMeet3dPhotoType.GREETING_BOT_AVATAR,
          photoData: greetingBotAvatarPhotoData,
        })
        .then(() => {
          toastMessageStore.addToastMessage(t("clientPanelMisc:newPhotoSaved"), "positive", t("success"));
        })
        .catch(error => {
          if (error.status === "error") {
            showError(error.message);
          }
        });
    }

    greetingBotSavePromise
      .then(greetingBotAvatarPhotoDeletePromise)
      .then(greetingBotAvatarPhotoCreatePromise)
      .then(() => {
        this.uiMeet3dPhotoStore.loadIndex().catch(error => showError(error.message));

        this.bindForm();
      });
  }

  onCustomFieldSave(customField) {
    const { showError } = this.props;

    if (!customField.isValid()) {
      showError("Unexpected invalid customField");
    }

    if (this.greetingBotStore.customFieldNameExists(customField)) {
      this.setState({ showCustomFieldNameWarning: true });

      return;
    }

    this.greetingBotStore.saveCustomField(customField);
    this.greetingBotStore.initializeCustomFieldFormStore({});
    // close modal for those cases when this method is called from an "Edit" context.
    this.setState({ showCustomFieldEdit: false });
  }

  onCustomFieldEdit(id) {
    const { customFields } = this.greetingBotStore;
    const customField = customFields.find(c => c.id === id);

    // since the customField has an id, it will be bound to the customFieldFormStore so later on we can
    // dump the updates into the right customField.
    this.greetingBotStore.initializeCustomFieldFormStore({ ...customField });
    this.setState({ showCustomFieldEdit: true });
  }

  onCustomFieldEditClose() {
    this.greetingBotStore.initializeCustomFieldFormStore({});
    this.setState({ showCustomFieldEdit: false });
  }

  getUiMeet3dGreetingBotDefaultValue(defaultValues, key, language) {
    const defaultValue = defaultValues.find(item => item.key === key);

    if (typeof defaultValue === "undefined") {
      return "";
    }

    return defaultValue[language];
  }

  bindForm() {
    const initialModel = {
      greetingBotAvatarPhotoData: "",
      deleteGreetingBotAvatarPhotoData: false,
    };

    this.formStore = new FormStore(initialModel);
  }

  render() {
    const { t, toastMessageStore } = this.props;
    const { showCustomFieldNameWarning, showCustomFieldEdit } = this.state;
    const {
      loading,
      loaded,
      isSaving: greetingBotSaving,
      formStore: greetingBotFormStore,
      user,
      customFieldFormStore,
      canSaveCustomField,
      customFields,
      customFieldsDirty,
    } = this.greetingBotStore;
    const { model, bindInput, setProperty, isDirty } = greetingBotFormStore;
    const {
      model: customFieldModel,
      bindInput: customFieldBindInput,
      setProperty: customFieldSetProperty,
    } = customFieldFormStore;
    const {
      isDirty: uiMeet3dPhotoDirty,
      model: uiMeet3dPhotoModel,
      setProperty: uiMeet3dPhotoSetProperty,
    } = this.formStore;
    const { isLoading: uiMeet3dPhotoLoading, isSaving: uiMeet3dPhotoSaving } = this.uiMeet3dPhotoStore;
    const {
      isLoading: uiMeet3dGreetingBotDefaultValueLoading,
      uiMeet3dGreetingBotDefaultValues,
    } = this.uiMeet3dGreetingBotDefaultValueStore;

    if (loading || !loaded || uiMeet3dPhotoLoading || uiMeet3dGreetingBotDefaultValueLoading) {
      return <ContentLoader />;
    }

    const validation = getValidationErrors({}, model);
    const someFormsDirty = isDirty || customFieldsDirty || uiMeet3dPhotoDirty;
    const isSaving = greetingBotSaving || uiMeet3dPhotoSaving;

    return (
      <>
        <LoadingWrapper loading={isSaving} message={t("saving")} />
        <div className="greeting-bot-outer-container">
          <Ref innerRef={this.ref}>
            <div>
              <StickyMenu
                contextRef={this.ref}
                headerText={t("clientPanelMisc:greetingBot")}
                button={
                  <div className="greeting-bot-save-btn-wrap">
                    <Button
                      primary
                      icon
                      disabled={!(validation.isValid && someFormsDirty)}
                      onClick={() => this.onSubmit()}
                    >
                      <Icon name="save" />
                      {t("saveChanges")}
                    </Button>
                  </div>
                }
              />
              <Segment>
                <GreetingBotGeneralForm
                  actualUser={user.actualUser}
                  model={model}
                  bindInput={bindInput}
                  setProperty={setProperty}
                  validation={validation}
                  uiMeet3dPhotoModel={uiMeet3dPhotoModel}
                  uiMeet3dPhotoSetProperty={uiMeet3dPhotoSetProperty}
                  uiMeet3dGreetingBotDefaultValues={uiMeet3dGreetingBotDefaultValues}
                  getUiMeet3dGreetingBotDefaultValueCallback={(defaultValues, key, language) =>
                    this.getUiMeet3dGreetingBotDefaultValue(defaultValues, key, language)
                  }
                  uiMeet3dPhotoStore={this.uiMeet3dPhotoStore}
                />
              </Segment>
              <Grid columns={2}>
                <Grid.Column width={8}>
                  <Segment>
                    <h2>{t("clientPanelMisc:customForm")}</h2>
                    <h4>{t("title")}</h4>
                    <Tab
                      panes={[
                        {
                          menuItem: t("english"),
                          render: () => (
                            <Tab.Pane>
                              <Form.Input
                                fluid
                                label={t("title")}
                                {...bindInput("action_form_title_en")}
                                value={model.action_form_title_en}
                                placeholder={this.getUiMeet3dGreetingBotDefaultValue(
                                  uiMeet3dGreetingBotDefaultValues,
                                  "actionFormTitle",
                                  "en",
                                )}
                              />
                            </Tab.Pane>
                          ),
                        },
                        {
                          menuItem: t("french"),
                          render: () => (
                            <Tab.Pane>
                              <Form.Input
                                fluid
                                label={t("title")}
                                {...bindInput("action_form_title_fr")}
                                value={model.action_form_title_fr}
                                placeholder={this.getUiMeet3dGreetingBotDefaultValue(
                                  uiMeet3dGreetingBotDefaultValues,
                                  "actionFormTitle",
                                  "fr",
                                )}
                              />
                            </Tab.Pane>
                          ),
                        },
                      ]}
                    />
                    <br />
                    <Modal
                      open={showCustomFieldNameWarning}
                      header={`${t("warning")}!`}
                      content={`${t("clientPanelMisc:customFieldLabelAlreadyInUse")} ${t(
                        "clientPanelMisc:pleaseUseADifferentOne",
                      )}`}
                      size="mini"
                      actions={[
                        {
                          key: "ok",
                          content: "Ok",
                          positive: true,
                          onClick: () => this.setState({ showCustomFieldNameWarning: false }),
                        },
                      ]}
                    />
                    <Tab
                      panes={[
                        {
                          menuItem: t("clientPanelMisc:suggestedFields"),
                          render: () => (
                            <Tab.Pane>
                              <GreetingBotSimpleFormBuilder
                                onFieldAdd={customField => this.onCustomFieldSave(customField)}
                              />
                            </Tab.Pane>
                          ),
                        },
                        {
                          menuItem: t("clientPanelMisc:customFields"),
                          render: () => (
                            <Tab.Pane>
                              <GreetingBotAdvancedFormBuilder
                                canSaveCustomField={canSaveCustomField}
                                bindInput={bindInput}
                                model={model}
                                customFieldBindInput={customFieldBindInput}
                                customFieldModel={customFieldModel}
                                customFieldSetProperty={customFieldSetProperty}
                                onCustomFieldSave={() =>
                                  this.onCustomFieldSave(this.greetingBotStore.getCustomFieldFromStore())
                                }
                                saveButtonLabel={t("clientPanelMisc:addField")}
                              />
                            </Tab.Pane>
                          ),
                        },
                      ]}
                    />
                  </Segment>
                </Grid.Column>
                <Grid.Column width={8} className="form-preview">
                  <Segment>
                    <Modal open={showCustomFieldEdit} onClose={() => this.onCustomFieldEditClose()}>
                      <Modal.Header>{t("clientPanelMisc:editCustomField")}</Modal.Header>
                      <Modal.Content>
                        <Modal.Description>
                          <GreetingBotAdvancedFormBuilder
                            isModal
                            canSaveCustomField={canSaveCustomField}
                            bindInput={bindInput}
                            model={model}
                            customFieldBindInput={customFieldBindInput}
                            customFieldModel={customFieldModel}
                            customFieldSetProperty={customFieldSetProperty}
                          />
                        </Modal.Description>
                        <Modal.Actions>
                          <Button
                            primary
                            disabled={!canSaveCustomField}
                            onClick={() => this.onCustomFieldSave(this.greetingBotStore.getCustomFieldFromStore())}
                          >
                            {t("update")}
                          </Button>
                          <Button secondary onClick={() => this.onCustomFieldEditClose()}>
                            {t("cancel")}
                          </Button>
                        </Modal.Actions>
                      </Modal.Content>
                    </Modal>
                    <CustomDndProvider>
                      <GreetingBotFormPreview
                        model={model}
                        customFields={customFields}
                        onCustomFieldRemove={id => this.greetingBotStore.removeCustomField(id)}
                        onCustomFieldEdit={id => this.onCustomFieldEdit(id)}
                        onCustomFieldHover={(sourceItem, targetItem) =>
                          this.greetingBotStore.reorder(sourceItem.id, targetItem.id)
                        }
                      />
                    </CustomDndProvider>
                  </Segment>
                </Grid.Column>
              </Grid>
            </div>
          </Ref>
        </div>
        <ToastMessageContainer toastMessageStore={toastMessageStore} />
      </>
    );
  }
}

GreetingBotPage.propTypes = {
  user: PropTypes.instanceOf(User).isRequired,
  userStore: PropTypes.instanceOf(UserStore).isRequired,
  t: PropTypes.func.isRequired,
  toastMessageStore: PropTypes.instanceOf(ToastMessageStore),
};

export default withTranslation()(ErrorWrapper(UiMeet3dLayoutWrapper(GreetingBotPage, UiMeet3dNav.GREETING_BOT)));
